import * as React from "react";
import { Link } from "react-router-dom";
import { Card, Tooltip, Button } from "@jhool-io/fe-components";
import { IRemit } from "../../types/remits.types";
import { formatDate, showMoneyInAppFormat } from "../../../../utils/helpers";
import {
    buildBatchRemitAdjustmentRows,
    getRemitClaimsListNavigateUrl,
} from "../../helpers/helpers.remits";
import InfoIcon from "../../../../icons/Info";
import BatchInsurancePayments from "../InsurancePayments/BatchInsurancePayments/BatchInsurancePayments";
import BulkPostPaymentsAction from "../InsurancePayments/BulkPostPaymentsAction/BulkPostPaymentsAction";
import RemitDocuments from "../RemitDocuments/RemitDocuments";
import RemitEOB from "../RemitEOB/RemitEOB";
import ListState from "../../../../components/ListState/ListState";

interface RemitBatchViewProps {
    remits: IRemit[];
}

export default function RemitBatchView({ remits }: RemitBatchViewProps) {
    const primaryRemit = remits[0];

    const totalInsCharged = remits.reduce(
        (a, r) => a + (r.insurance_charged ?? 0),
        0
    );

    const patientCharged = primaryRemit.patient_charged ?? 0;
    const patientPaid = primaryRemit.patient_paid ?? 0;
    const patientWriteOff = primaryRemit.patient_writeoff ?? 0;
    const totalPr100 = primaryRemit.pr100_collected ?? 0;

    const adjustmentRows = buildBatchRemitAdjustmentRows(remits);
    const totalAdjustments = adjustmentRows.reduce((a, x) => a + x.amount, 0);
    const adjustmentCpts = Array.from(
        new Set(adjustmentRows.map((a) => a.cpt))
    );

    const checkRef = primaryRemit.remit_batch_payment_ref
        ? `#${primaryRemit.remit_batch_payment_ref}`
        : "";

    const claimsUrl = getRemitClaimsListNavigateUrl(
        primaryRemit.remittance_batch_id
    );

    const checkDate = primaryRemit.remit_received_date
        ? formatDate(
              primaryRemit.remit_received_date.replace("+00:00", ""),
              true
          )
        : "--";

    return (
        <div className="flex flex-col gap-y-[--spacing-header]">
            <Card classNames="p-0 rounded-md overflow-x-auto">
                <div className="px-[--padding-card-x] py-[--padding-card-y] [border-bottom:1px_solid_var(--color-stroke)]">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                        <span className="font-bold text-base">
                            {primaryRemit.insurance_provider_name ||
                                "Insurance"}
                        </span>
                        <div className="ml-auto flex items-center gap-x-4">
                            {checkRef ? (
                                <div className="flex items-center gap-x-1">
                                    <span className="text-textSec text-caption">
                                        Check
                                    </span>
                                    <Link
                                        to={claimsUrl}
                                        state={{ from: "batch_remit" }}
                                        className="font-bold text-base text-textLink hover:underline"
                                    >
                                        {checkRef}
                                    </Link>
                                </div>
                            ) : null}
                            <div className="flex items-center gap-x-1">
                                <span className="text-textSec text-caption">
                                    Check date
                                </span>
                                <span className="font-bold text-base">
                                    {checkDate}
                                </span>
                            </div>

                            <span className="font-bold text-base">
                                {remits.length} CPT
                                {remits.length === 1 ? "" : "s"}
                            </span>
                            <div className="flex items-center gap-x-1">
                                <span className="text-textSec text-caption">
                                    Pt. charged
                                </span>
                                <span className="font-bold text-base">
                                    {showMoneyInAppFormat(patientCharged)}
                                </span>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <span className="text-textSec text-caption">
                                    Pt. paid
                                </span>
                                <span className="font-bold text-base">
                                    {showMoneyInAppFormat(patientPaid)}
                                </span>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <span className="text-textSec text-caption">
                                    Pt. write-off
                                </span>
                                <span className="font-bold text-base">
                                    {showMoneyInAppFormat(patientWriteOff)}
                                </span>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <span className="text-textSec text-caption">
                                    PR100
                                </span>
                                <span className="font-bold text-base">
                                    {showMoneyInAppFormat(totalPr100)}
                                </span>
                            </div>
                            <div className="flex items-center gap-x-1">
                                <span className="text-textSec text-caption">
                                    UCR
                                </span>
                                <span className="font-bold text-base">
                                    {primaryRemit.ucr || "--"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-6 gap-x-1 bg-[--card-bg-dark] [border-bottom:1px_solid_var(--color-stroke)] px-[--padding-card-x] py-[--padding-card-y] text-textSec text-caption font-semibold uppercase min-w-[1000px]">
                    <span>Type</span>
                    <span>Charged</span>
                    <span>Expected</span>
                    <span>Paid</span>
                    <span>Write-off</span>
                    <span>Interest</span>
                </div>

                {remits.map((remit) => {
                    const cpt = remit.remittance_cpt_code || remit.cpt_code;
                    return (
                        <React.Fragment key={remit.remittance_id}>
                            <div className="px-[--padding-card-x] py-8 bg-[--card-bg-gray] [border-bottom:1px_solid_var(--color-stroke)] text-xs font-semibold uppercase tracking-wide text-textSec min-w-[1000px]">
                                CPT {cpt || "--"}
                            </div>
                            <div className="grid grid-cols-6 gap-x-1 px-[--padding-card-x] py-[--padding-card-y] text-text text-base font-semibold uppercase [border-bottom:1px_solid_var(--color-stroke)] min-w-[1000px]">
                                <span className="text-textSec font-medium">
                                    Insurance
                                </span>
                                <span>
                                    {showMoneyInAppFormat(
                                        remit.insurance_charged ?? 0
                                    )}
                                </span>
                                <span className="flex items-center gap-x-2">
                                    {remit.insurance_expected
                                        ? showMoneyInAppFormat(
                                              remit.insurance_expected
                                          )
                                        : "--"}
                                    {remit.insurance_expected >
                                        remit.insurance_paid && (
                                        <Tooltip content="Expected amount is higher than paid amount">
                                            <Button
                                                variant="normal"
                                                size="auto"
                                            >
                                                <InfoIcon />
                                            </Button>
                                        </Tooltip>
                                    )}
                                </span>
                                <span className="text-textLink">
                                    {showMoneyInAppFormat(
                                        remit.insurance_paid || 0
                                    )}
                                </span>
                                <span>
                                    {showMoneyInAppFormat(
                                        remit.insurance_writeoff || 0
                                    )}
                                </span>
                                <span>
                                    {showMoneyInAppFormat(
                                        (remit.interest_collected || 0) -
                                            (remit.interest_refund || 0)
                                    )}
                                </span>
                            </div>
                        </React.Fragment>
                    );
                })}

                <div className="flex flex-col gap-y-2 px-[--padding-card-x] py-[--padding-card-y] [border-top:1px_solid_var(--color-stroke)] rounded-bl-md rounded-br-md">
                    <div className="flex items-center justify-between">
                        <span className="text-textSec text-caption uppercase font-semibold">
                            Total Charged
                        </span>
                        <span className="font-bold text-base">
                            {showMoneyInAppFormat(totalInsCharged)}
                        </span>
                    </div>
                </div>
            </Card>

            <Card classNames="p-24 overflow-x-auto">
                <div className="flex items-center justify-between mb-[--spacing-header]">
                    <p className="text-base font-bold">Insurance Payments</p>
                    <BulkPostPaymentsAction />
                </div>
                <BatchInsurancePayments remits={remits} />
            </Card>

            <Card classNames="p-24 overflow-x-auto max-w-full">
                <p className="text-base font-bold mb-[--spacing-header]">
                    Adjustments
                </p>
                {adjustmentRows.length === 0 ? (
                    <ListState
                        stateHelperText="Adjustments will appear here once they are added"
                        isError={false}
                        emptyMessage="No adjustments on this batch"
                        context="general"
                    />
                ) : (
                    <div>
                        <div className="grid grid-cols-12 w-full text-textSec text-xs font-semibold leading-7 gap-[6px] p-12 [border:1px_solid_var(--color-stroke)] rounded-t-md">
                            <p className="col-span-1">No.</p>
                            <p className="col-span-2">REASON CODE</p>
                            <p className="col-span-7">DESCRIPTION</p>
                            <p className="col-span-2">AMOUNT</p>
                        </div>
                        {adjustmentCpts.map((cpt) => {
                            const cptRows = adjustmentRows.filter(
                                (a) => a.cpt === cpt
                            );
                            return (
                                <div key={`adj-cpt-${cpt}`}>
                                    <div className="px-12 py-8 bg-[--card-bg-gray] [border-left:1px_solid_var(--color-stroke)] [border-right:1px_solid_var(--color-stroke)] [border-bottom:1px_solid_var(--color-stroke)] text-xs font-semibold uppercase tracking-wide text-textSec">
                                        CPT {cpt || "--"}
                                    </div>
                                    {cptRows.map((entry) => {
                                        const rowNumber =
                                            adjustmentRows.indexOf(entry) + 1;
                                        return (
                                            <div
                                                key={entry.id}
                                                className="grid grid-cols-12 items-center w-full [border-left:1px_solid_var(--color-stroke)] [border-bottom:1px_solid_var(--color-stroke)] [border-right:1px_solid_var(--color-stroke)] border-t-0 p-12 text-left text-sm leading-2 gap-6"
                                            >
                                                <p className="col-span-1">
                                                    {rowNumber}
                                                </p>
                                                <p className="col-span-2">
                                                    {`${entry.groupCode}-${
                                                        entry.code || "--"
                                                    }`}
                                                </p>
                                                <p className="col-span-7">
                                                    {entry.description || "--"}
                                                </p>
                                                <p className="col-span-2">
                                                    {showMoneyInAppFormat(
                                                        entry.amount
                                                    )}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                        <div className="grid grid-cols-12 items-center w-full [border:1px_solid_var(--color-stroke)] ![border-top:none] rounded-b-md p-12 text-sm leading-8 gap-6 font-bold">
                            <p className="col-span-1" />
                            <p className="col-span-2" />
                            <p className="col-span-7">Total:</p>
                            <p className="col-span-2">
                                {showMoneyInAppFormat(totalAdjustments)}
                            </p>
                        </div>
                    </div>
                )}
            </Card>

            <RemitEOB
                remittanceId={primaryRemit.remittance_id}
                remitSource={primaryRemit.remit_source}
                batchEobUrl={primaryRemit.remit_batch_eob_url}
            />

            <RemitDocuments
                remittanceId={primaryRemit.remittance_id}
                remitName={`${primaryRemit.client.first_name} ${primaryRemit.client.last_name}`}
            />
        </div>
    );
}
